import type { AttributionModel as AttributionDistribution } from "@redotech/crdb-enums/attribution/attribution-model";
import { useHandler } from "@redotech/react-util/hook";
import {
  ATTRIBUTION_TIME_UNITS,
  AttributionEvidenceType,
  MAX_ATTRIBUTION_WINDOW_DAYS,
  type Duration,
  type EvidenceConfig,
  type OutcomeAttributionConfig,
} from "@redotech/redo-attribution-common/attribution-settings";
import { durationToSeconds, TimeUnit } from "@redotech/redo-common-model/time";
import { useTranslation } from "@redotech/redo-i18n";
import {
  attributionWindowUnitLabel,
  distributionOptions,
  EVIDENCE_SECTION,
  evidenceMeta,
  EvidenceSection,
  evidenceSectionMeta,
} from "@redotech/redo-merchant-app-common/attribution/evidence-settings";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { Text } from "@redotech/redo-web/text";
import { Fragment, memo } from "react";

type WindowUnit = Duration["unit"];

/** The tab's own wording; the settings page's also names Commerce Agent. */
const SECTION_DESCRIPTIONS: Partial<Record<EvidenceSection, string>> = {
  [EvidenceSection.EMAIL]: "Campaign and automation emails",
  [EvidenceSection.SMS]: "Campaign and automation text messages",
};

const MAX_WINDOW_SECONDS = durationToSeconds(
  MAX_ATTRIBUTION_WINDOW_DAYS,
  TimeUnit.DAY,
);

function maxWindowValue(unit: WindowUnit): number {
  return Math.floor(MAX_WINDOW_SECONDS / durationToSeconds(1, unit));
}

/** Holds typed input to a whole window no longer than the longest allowed. */
function clampWindow({ value, unit }: Duration): Duration {
  const whole = Number.isFinite(value) ? Math.trunc(value) : 1;
  return { unit, value: Math.min(Math.max(whole, 1), maxWindowValue(unit)) };
}

/** The attribution settings page's controls, for this view's report only. */
export const AttributionControls = memo(function AttributionControls({
  config,
  setConfig,
  includeBotClicks,
  setIncludeBotClicks,
}: {
  /** Undefined until the saved settings load, as is `includeBotClicks`. */
  config: OutcomeAttributionConfig | undefined;
  setConfig(config: OutcomeAttributionConfig): void;
  includeBotClicks: boolean | undefined;
  setIncludeBotClicks(includeBotClicks: boolean): void;
}) {
  useTranslation();
  const distributions: RedoListItem<AttributionDistribution>[] =
    distributionOptions().map(({ value, text }) => ({
      id: value,
      type: "text",
      text,
      value,
    }));
  const setEvidence = useHandler(
    (evidenceType: AttributionEvidenceType, next: EvidenceConfig) => {
      if (config) {
        setConfig({
          ...config,
          evidence: { ...config.evidence, [evidenceType]: next },
        });
      }
    },
  );
  return (
    <Flex dir="column" gap="xl" w="full">
      <RedoSingleSelectDropdownInput
        disabled={!config}
        fullWidth
        options={distributions}
        optionSelected={({ item }) => {
          if (config) {
            setConfig({ ...config, defaultDistribution: item.value });
          }
        }}
        selectedItem={distributions.find(
          (option) => option.value === config?.defaultDistribution,
        )}
        size="sm"
      />
      <InspectorField
        label="Include bot clicks"
        labelPosition="left"
        spaceBetween
      >
        <RedoToggle
          disabled={includeBotClicks === undefined}
          setValue={setIncludeBotClicks}
          value={includeBotClicks ?? false}
        />
      </InspectorField>
      <Flex dir="column" gap="xxs">
        <Text fontSize="sm" fontWeight="semibold">
          Evidence
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Windows look backward from a purchase.
        </Text>
      </Flex>
      {config && (
        <Flex dir="column" gap="lg">
          {Object.values(EvidenceSection).map((section) => (
            <EvidenceSectionFields
              evidence={config.evidence}
              key={section}
              section={section}
              setEvidence={setEvidence}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
});

const EvidenceSectionFields = memo(function EvidenceSectionFields({
  section,
  evidence,
  setEvidence,
}: {
  section: EvidenceSection;
  evidence: OutcomeAttributionConfig["evidence"];
  setEvidence(
    evidenceType: AttributionEvidenceType,
    next: EvidenceConfig,
  ): void;
}) {
  useTranslation();
  const meta = evidenceSectionMeta()[section];
  const names = evidenceMeta();
  const evidenceTypes = Object.values(AttributionEvidenceType).filter(
    (evidenceType) => EVIDENCE_SECTION[evidenceType] === section,
  );
  return (
    <Flex
      borderColor="primary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      p="lg"
    >
      <InspectorSection
        defaultOpen={false}
        description={SECTION_DESCRIPTIONS[section] ?? meta.description}
        label={meta.name}
      >
        {evidenceTypes.map((evidenceType) => {
          const config = evidence[evidenceType];
          if (!config) {
            return null;
          }
          return (
            <Fragment key={evidenceType}>
              <Divider />
              <EvidenceFields
                config={config}
                editableWindow={names[evidenceType].editableWindow}
                name={names[evidenceType].name}
                setConfig={(next) => setEvidence(evidenceType, next)}
              />
            </Fragment>
          );
        })}
      </InspectorSection>
    </Flex>
  );
});

const EvidenceFields = memo(function EvidenceFields({
  name,
  config,
  editableWindow,
  setConfig,
}: {
  name: string;
  config: EvidenceConfig;
  editableWindow: boolean;
  setConfig(config: EvidenceConfig): void;
}) {
  return (
    <Flex dir="column" gap="md">
      <InspectorField label={name} labelPosition="left" spaceBetween>
        <RedoToggle
          setValue={(enabled) => setConfig({ ...config, enabled })}
          value={config.enabled}
        />
      </InspectorField>
      {config.enabled && editableWindow && (
        <WindowEditor
          setWindow={(window) => setConfig({ ...config, window })}
          window={config.window}
        />
      )}
    </Flex>
  );
});

const WindowEditor = memo(function WindowEditor({
  window,
  setWindow,
}: {
  window: Duration;
  setWindow(window: Duration): void;
}) {
  useTranslation();
  const units: RedoListItem<WindowUnit>[] = ATTRIBUTION_TIME_UNITS.map(
    (unit) => ({
      id: unit,
      type: "text",
      text: attributionWindowUnitLabel(unit),
      value: unit,
    }),
  );
  return (
    <InspectorField label="Within" labelPosition="left">
      <Flex align="center" gap="md">
        <RedoIncrementDecrement
          max={maxWindowValue(window.unit)}
          min={1}
          setValue={(value) => setWindow(clampWindow({ ...window, value }))}
          size="small"
          value={window.value}
        />
        <RedoSingleSelectDropdownInput
          fitToAnchor={false}
          flexGrow={false}
          options={units}
          optionSelected={({ item }) =>
            setWindow(clampWindow({ ...window, unit: item.value }))
          }
          selectedItem={units.find((option) => option.value === window.unit)}
          size="xs"
        />
      </Flex>
    </InspectorField>
  );
});
